import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Chart } from 'angular-highcharts';

export interface MetricData {
  id: number;
  name: string;
  display_name: string;
  desc?: string;
  description?: string;
  actual_value: any; // can be an array of historical values or a formatted scalar/display value
  unit_name?: string;
  change?: any;
  variance_percent?: number;
  status?: string;
  trend?: Chart;
  overviewTrend?: Chart;
  value?: any;
  groupName?: string;
  groupLength?: number;
}

  export interface QuadrantData {
    id: number;
    name: string;
    display_name: string;
    metrics?: MetricData[];
    groups?: { group: string; metrics: MetricData[] }[];
  }

import textConfiguration from "src/assets/static-text-configuration.json";
@Component({
  selector: 'app-bvt-card',
  templateUrl: './bvt-card.component.html',
  styleUrls: ['./bvt-card.component.scss']
})
export class BvtCardComponent implements OnInit {

  @Input() bvtData: any;
  commonText: any = (textConfiguration as any).common;
  @Input() quadrantData: QuadrantData;
  @Input() cardTitle: string = '';
  @Input() cardIcon: string = 'fas fa-user-friends';
  @Input() cardIconClass: string = 'user-icon';
  @Input() columnBgClass: string = 'column-bg-1';
  @Input() quadrantId: number;
  @Input() showQuadrantInfo: boolean = false;
  @Input() noDataMessage: string = 'No data found';

  @Output() metricClick = new EventEmitter<{
    quadrantId: number;
    metric: MetricData;
    quadrantData: MetricData[];
    quadrantName: string;
  }>();
  @Output() pulseClick = new EventEmitter<string>();
  @Output() quadrantInfoClick = new EventEmitter<{
    quadrantId: number;
    quadrantName: string;
  }>();
  @Output() metricOrderChanged = new EventEmitter<{
    quadrantId: number;
    metrics: MetricData[];
  }>();

  dragEnableFlags: any[] = [];
  processedMetrics: MetricData[] = [];



  constructor() { }

  ngOnInit(): void {
    this.processMetricsData();
    this.initializeDragFlags();
  }

  ngOnChanges(): void {
    if (this.quadrantData) {
      this.processMetricsData();
      this.initializeDragFlags();
    }
  }


  private processMetricsData(): void {
  if (!this.quadrantData?.groups) {
    this.processedMetrics = [];
    return;
  }

  this.processedMetrics = [];

  this.quadrantData.groups.forEach(group => {
    const metricsWithGroup = group.metrics.map((metric, idx) => ({
      ...metric,
      groupName: group.group,
      groupLength: group.metrics.length,
      desc: metric.desc || metric.description || '',
      trend: metric.trend || this.generateTrendChart(metric),
      change: this.getChange(metric),
      actual_value: this.getActualValue(metric),
      variance_per: this.roundToTwoDecimals(metric.variance_percent || 0)
    }));

    this.processedMetrics.push(...metricsWithGroup);
  });
}
isFirstMetricOfGroup(index: number): boolean {
  if (index === 0) return true;
  return this.processedMetrics[index].groupName !== this.processedMetrics[index - 1].groupName;
}

  private initializeDragFlags(): void {
    this.dragEnableFlags = this.processedMetrics.map(() => true);
  }

  private generateTrendChart(metric: any): Chart | undefined {
    if (!metric.actual_value || !Array.isArray(metric.actual_value)) {
      return undefined;
    }

    const metricValues = metric.actual_value.map((item: any) => 
      this.roundToTwoDecimals(item.actual_value || 0)
    );
    const monthLabels = metric.actual_value.map((item: any) => 
      item.month_short || ''
    );

    return this.returnSparkLineChart([{ data: metricValues }], 'line', monthLabels);
  }

  private returnSparkLineChart(seriesData: any, type: any, categories: any): Chart {
    return new Chart({
      title: { text: '' },
      credits: false,
      chart: {
        type,
        width: 120,
        height: 30,
        margin: [0, 0, 8, 0],
        style: { overflow: 'visible' },
        skipClone: true,
      },
      yAxis: {
        endOnTick: false,
        startOnTick: false,
        labels: { enabled: false },
        title: { text: null },
        tickPositions: [0],
      },
      xAxis: {
        categories,
        lineWidth: 0,
        minorGridLineWidth: 0,
        lineColor: 'transparent',
        labels: { enabled: false },
        title: { text: null },
        startOnTick: false,
        endOnTick: false,
        tickPositions: [],
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          marker: { enabled: false }
        }
      },
      tooltip: {
        enabled: true,
        outside: true,
        formatter: function(this: any): string {
          return `<b>${this.x}</b><br/>${this.y.toLocaleString()}`;
        }
      },
      series: seriesData,
      exporting: { enabled: false },
    } as any);
  }

  private getActualValue(metric: any): string {
    const lastValue = metric.actual_value?.[metric.actual_value.length - 1];
    if (!lastValue) return '0.00';

    let value = 0;
    if (['attrition_currentmonth', 'krr_tt', 'attrition_ltm', 'attrition_ytd'].includes(metric.name)) {
      value = lastValue.percentage || lastValue.actual_value;
    } else if (metric.name === 'experience_profile') {
      value = lastValue.data?.ratio || 0;
    } else {
      value = lastValue.actual_value || 0;
    }

    return this.formatNumber(this.roundToTwoDecimals(value), 2);
  }

  private getChange(metric: any): any {
    if (['experience_profile', 'req_aging', 'gdp_survey_rating', 'bvt_survey_rating'].includes(metric.name)) {
      return 'false';
    }
    return metric.variance || 0;
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private formatNumber(number: any, decimalPlaces: number): string {
    if (!isNaN(number)) {
      return number.toFixed(decimalPlaces);
    }
    return number;
  }

  getFormattedValue(val: number): string {
    if (!val) return '0';
    
    const absVal = Math.abs(val);
    if (absVal >= 1e9) {
      return (val / 1e9).toFixed(1) + 'B';
    } else if (absVal >= 1e6) {
      return (val / 1e6).toFixed(1) + 'M';
    } else if (absVal >= 1e3) {
      return (val / 1e3).toFixed(1) + 'K';
    } else {
      const formattedVal = absVal.toFixed(2);
      return formattedVal.toString();
    }
  }


  hasTrendData(metric: MetricData): boolean {
    return !!(
      //@ts-ignore
      metric?.trend?.options?.xAxis?.categories?.length ||
      //@ts-ignore
      metric?.trend?.options?.xAxis?.[0]?.categories?.length
    );
  }
}
